import React, { useState, useEffect, useCallback } from "react";
import {
    Webhook,
    Plus,
    Trash2,
    AlertCircle,
    Loader2,
    Globe,
    CheckCircle2,
    XCircle,
    Play,
    ChevronDown,
    ChevronUp,
    Clock,
    Edit2,
    Check
} from "lucide-react";
import {
    fetchOrgWebhooks,
    createOrgWebhook,
    deleteOrgWebhook,
    testOrgWebhook,
    updateOrgWebhook,
    fetchWebhookDeliveries
} from "../../api/organizations";
import { isLimitReached, WEBHOOK_EVENTS } from "../../utils/organizationHelpers";

const WebhookCard = ({
    webhook,
    onTest,
    onDelete,
    onToggle,
    organization,
    canEdit = false
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [deliveries, setDeliveries] = useState([]);
    const [isLoadingDeliveries, setIsLoadingDeliveries] = useState(false);

    const handleTest = async () => {
        setIsTesting(true);
        setTestResult(null);
        try {
            const result = await onTest(webhook.id);
            setTestResult({ success: true, message: "Test webhook sent successfully!" });
        } catch (err) {
            setTestResult({ success: false, message: err.message || "Test failed" });
        } finally {
            setIsTesting(false);
        }
    };

    const loadDeliveries = async () => {
        if (deliveries.length > 0 || !organization?.id) return;
        setIsLoadingDeliveries(true);
        try {
            const response = await fetchWebhookDeliveries(organization.id, webhook.id, { limit: 10 });
            const data = response?.data?.data || response?.data || {};
            setDeliveries(Array.isArray(data.deliveries) ? data.deliveries : []);
        } catch (err) {
            console.error("Failed to load deliveries:", err);
        } finally {
            setIsLoadingDeliveries(false);
        }
    };

    const handleExpandToggle = () => {
        if (!isExpanded) {
            loadDeliveries();
        }
        setIsExpanded(!isExpanded);
    };

    return (
        <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl overflow-hidden">
            {/* Main Content */}
            <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            webhook.enabled
                                ? "bg-gradient-to-br from-emerald-400 to-emerald-600"
                                : "bg-gradient-to-br from-slate-400 to-slate-600"
                        }`}>
                            <Webhook className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h4 className="dark:text-white text-slate-900 font-semibold flex items-center gap-2">
                                {webhook.name}
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                    webhook.enabled
                                        ? "dark:bg-emerald-500/10 bg-emerald-50 dark:text-emerald-400 text-emerald-600"
                                        : "dark:bg-slate-700 bg-slate-100 dark:text-slate-400 text-slate-500"
                                }`}>
                                    {webhook.enabled ? "Active" : "Disabled"}
                                </span>
                            </h4>
                            <div className="flex items-center gap-1 text-xs dark:text-slate-400 text-slate-500 mt-1">
                                <Globe className="w-3 h-3" />
                                <span className="font-mono truncate max-w-xs">{webhook.url}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {canEdit && (
                            <>
                                <button
                                    onClick={handleTest}
                                    disabled={isTesting || !webhook.enabled}
                                    className="p-2 dark:text-slate-400 text-slate-500 hover:text-teal-500 dark:hover:text-teal-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Test webhook"
                                >
                                    {isTesting ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Play className="w-4 h-4" />
                                    )}
                                </button>
                                <button
                                    onClick={() => onDelete(webhook)}
                                    className="p-2 dark:text-slate-400 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                    title="Delete webhook"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Events */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                    {webhook.events?.map((eventId) => {
                        const event = WEBHOOK_EVENTS.find(e => e.id === eventId);
                        return (
                            <span
                                key={eventId}
                                className="px-2 py-1 dark:bg-slate-800 bg-slate-100 rounded text-xs dark:text-slate-300 text-slate-600"
                            >
                                {event?.label || eventId}
                            </span>
                        );
                    })}
                </div>

                {/* Test Result */}
                {testResult && (
                    <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${
                        testResult.success
                            ? "bg-emerald-500/10 text-emerald-400"
                            : "bg-red-500/10 text-red-400"
                    }`}>
                        {testResult.success ? (
                            <CheckCircle2 className="w-4 h-4" />
                        ) : (
                            <XCircle className="w-4 h-4" />
                        )}
                        {testResult.message}
                    </div>
                )}

                {/* Toggle Expand */}
                <button
                    onClick={handleExpandToggle}
                    className="flex items-center gap-1 text-xs dark:text-slate-400 text-slate-500 hover:text-teal-500 dark:hover:text-teal-400 mt-3"
                >
                    {isExpanded ? (
                        <>
                            <ChevronUp className="w-3 h-3" />
                            Hide deliveries
                        </>
                    ) : (
                        <>
                            <ChevronDown className="w-3 h-3" />
                            Show recent deliveries
                        </>
                    )}
                </button>
            </div>

            {/* Deliveries Section */}
            {isExpanded && (
                <div className="border-t dark:border-slate-800 border-slate-200 p-4">
                    {isLoadingDeliveries ? (
                        <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-5 h-5 text-teal-500 animate-spin" />
                        </div>
                    ) : deliveries.length === 0 ? (
                        <p className="text-sm dark:text-slate-500 text-slate-400 text-center py-4">
                            No deliveries yet
                        </p>
                    ) : (
                        <div className="space-y-2">
                            <h5 className="text-xs font-semibold dark:text-slate-400 text-slate-500 uppercase tracking-wider mb-2">
                                Recent Deliveries
                            </h5>
                            {deliveries.map((delivery) => (
                                <div
                                    key={delivery.id}
                                    className="flex items-center justify-between p-2 dark:bg-slate-800/50 bg-slate-50 rounded-lg text-xs"
                                >
                                    <div className="flex items-center gap-2">
                                        {delivery.status === "success" ? (
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                        ) : (
                                            <XCircle className="w-3.5 h-3.5 text-red-400" />
                                        )}
                                        <span className="dark:text-slate-300 text-slate-600">
                                            {delivery.event}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="dark:text-slate-500 text-slate-400">
                                            {delivery.status_code}
                                        </span>
                                        <span className="flex items-center gap-1 dark:text-slate-500 text-slate-400">
                                            <Clock className="w-3 h-3" />
                                            {delivery.delivered_at
                                                ? new Date(delivery.delivered_at).toLocaleString()
                                                : "—"}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const CreateWebhookForm = ({ onSubmit, onCancel, isSubmitting }) => {
    const [name, setName] = useState("");
    const [url, setUrl] = useState("");
    const [selectedEvents, setSelectedEvents] = useState([]);

    const toggleEvent = (eventId) => {
        setSelectedEvents((prev) =>
            prev.includes(eventId)
                ? prev.filter((e) => e !== eventId)
                : [...prev, eventId]
        );
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!name.trim() || !url.trim() || selectedEvents.length === 0) return;
        onSubmit({
            name: name.trim(),
            url: url.trim(),
            events: selectedEvents,
            enabled: true
        });
    };

    return (
        <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6">
            <h4 className="text-lg font-semibold dark:text-white text-slate-900 mb-4">Create New Webhook</h4>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-1.5">
                        Webhook Name <span className="text-red-400">*</span>
                    </label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g., Production Notifications"
                        className="w-full px-4 py-2 dark:bg-slate-950 bg-slate-50 border dark:border-slate-700 border-slate-300 rounded-lg text-sm dark:text-white text-slate-900 focus:ring-1 focus:ring-teal-500 outline-none"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-1.5">
                        Endpoint URL <span className="text-red-400">*</span>
                    </label>
                    <input
                        type="url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://your-server.com/webhook"
                        className="w-full px-4 py-2 dark:bg-slate-950 bg-slate-50 border dark:border-slate-700 border-slate-300 rounded-lg text-sm dark:text-white text-slate-900 focus:ring-1 focus:ring-teal-500 outline-none"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                        Events <span className="text-red-400">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        {WEBHOOK_EVENTS.map((event) => (
                            <label
                                key={event.id}
                                className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-colors ${
                                    selectedEvents.includes(event.id)
                                        ? "dark:bg-teal-500/10 bg-teal-50 border-teal-500/50 dark:border-teal-500/50"
                                        : "dark:bg-slate-800/50 bg-slate-50 border-slate-200 dark:border-slate-700"
                                } border`}
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedEvents.includes(event.id)}
                                    onChange={() => toggleEvent(event.id)}
                                    className="sr-only"
                                />
                                <div className={`w-4 h-4 rounded flex items-center justify-center border ${
                                    selectedEvents.includes(event.id)
                                        ? "bg-teal-600 border-teal-600"
                                        : "dark:border-slate-600 border-slate-300"
                                }`}>
                                    {selectedEvents.includes(event.id) && (
                                        <Check className="w-3 h-3 text-white" />
                                    )}
                                </div>
                                <div>
                                    <p className="text-sm dark:text-white text-slate-900 font-medium">
                                        {event.label}
                                    </p>
                                    <p className="text-xs dark:text-slate-400 text-slate-500">
                                        {event.description}
                                    </p>
                                </div>
                            </label>
                        ))}
                    </div>
                </div>
                <div className="flex gap-3 pt-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="flex-1 px-4 py-2 dark:bg-slate-800 bg-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 dark:text-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting || !name.trim() || !url.trim() || selectedEvents.length === 0}
                        className="flex-1 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {isSubmitting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Webhook className="w-4 h-4" />
                        )}
                        Create Webhook
                    </button>
                </div>
            </form>
        </div>
    );
};

const OrgWebhooksTab = ({ organization, limits, canEdit = false }) => {
    const [webhooks, setWebhooks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    // Delete state
    const [webhookToDelete, setWebhookToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const canCreate = canEdit && !isLimitReached(webhooks.length, limits.webhooks);

    const loadWebhooks = useCallback(async () => {
        if (!organization?.id) return;
        setIsLoading(true);
        setError("");
        try {
            const response = await fetchOrgWebhooks(organization.id);
            const webhooksData = response?.data?.data || response?.data || {};
            setWebhooks(Array.isArray(webhooksData.webhooks) ? webhooksData.webhooks : []);
        } catch (err) {
            console.error("Failed to load webhooks:", err);
            setError("Unable to load webhooks.");
        } finally {
            setIsLoading(false);
        }
    }, [organization?.id]);

    useEffect(() => {
        loadWebhooks();
    }, [loadWebhooks]);

    const handleCreateWebhook = async (data) => {
        setIsCreating(true);
        setError("");
        try {
            await createOrgWebhook(organization.id, data);
            setShowCreateForm(false);
            loadWebhooks();
        } catch (err) {
            setError(err?.message || "Failed to create webhook.");
        } finally {
            setIsCreating(false);
        }
    };

    const handleTestWebhook = async (webhookId) => {
        return testOrgWebhook(organization.id, webhookId);
    };

    const handleToggleWebhook = async (webhook) => {
        try {
            await updateOrgWebhook(organization.id, webhook.id, {
                enabled: !webhook.enabled
            });
            loadWebhooks();
        } catch (err) {
            setError(err?.message || "Failed to update webhook.");
        }
    };

    const handleDeleteWebhook = async () => {
        if (!webhookToDelete) return;
        setIsDeleting(true);
        setError("");
        try {
            await deleteOrgWebhook(organization.id, webhookToDelete.id);
            setWebhookToDelete(null);
            loadWebhooks();
        } catch (err) {
            setError(err?.message || "Failed to delete webhook.");
        } finally {
            setIsDeleting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-xl font-bold dark:text-white text-slate-900">Webhooks</h3>
                    <p className="text-sm dark:text-slate-400 text-slate-500">
                        {webhooks.length} of {limits.webhooks} webhooks used
                    </p>
                </div>
                {!showCreateForm && (
                    <button
                        onClick={() => setShowCreateForm(true)}
                        disabled={!canCreate}
                        className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        New Webhook
                    </button>
                )}
            </div>

            {/* Permission Warning */}
            {!canEdit && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-2 text-sm text-amber-400">
                    <AlertCircle className="w-4 h-4" />
                    Only organization <strong className="mx-1">admins</strong> and <strong className="mx-1">owners</strong> can manage webhooks.
                </div>
            )}

            {/* Limit Warning */}
            {canEdit && isLimitReached(webhooks.length, limits.webhooks) && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-2 text-sm text-amber-400">
                    <AlertCircle className="w-4 h-4" />
                    Webhook limit reached. Upgrade your plan to create more webhooks.
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-400">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                </div>
            )}

            {/* Create Form */}
            {showCreateForm && (
                <CreateWebhookForm
                    onSubmit={handleCreateWebhook}
                    onCancel={() => setShowCreateForm(false)}
                    isSubmitting={isCreating}
                />
            )}

            {/* Webhooks List */}
            {!showCreateForm && (
                <>
                    {webhooks.length === 0 ? (
                        <div className="text-center py-12 dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl">
                            <Webhook className="w-12 h-12 mx-auto mb-3 dark:text-slate-600 text-slate-300" />
                            <p className="dark:text-slate-400 text-slate-500">No webhooks yet</p>
                            {canCreate && (
                                <button
                                    onClick={() => setShowCreateForm(true)}
                                    className="mt-4 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium transition-colors"
                                >
                                    Create your first webhook
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {webhooks.map((webhook) => (
                                <WebhookCard
                                    key={webhook.id}
                                    webhook={webhook}
                                    onTest={handleTestWebhook}
                                    onDelete={setWebhookToDelete}
                                    onToggle={handleToggleWebhook}
                                    organization={organization}
                                    canEdit={canEdit}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Delete Confirmation Modal */}
            {webhookToDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-sm shadow-2xl p-6">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-12 h-12 bg-red-500/10 text-red-400 rounded-full flex items-center justify-center">
                                <Trash2 className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold dark:text-white text-slate-900">Delete Webhook?</h3>
                                <p className="text-sm dark:text-slate-400 text-slate-500 mt-2">
                                    Are you sure you want to delete <span className="font-semibold dark:text-white text-slate-900">{webhookToDelete?.name}</span>?
                                    This action cannot be undone.
                                </p>
                            </div>
                            <div className="flex gap-3 w-full mt-2">
                                <button
                                    onClick={() => setWebhookToDelete(null)}
                                    className="flex-1 px-4 py-2 dark:bg-slate-800 bg-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 dark:text-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteWebhook}
                                    disabled={isDeleting}
                                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isDeleting ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        "Delete"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrgWebhooksTab;
